import { CheckIcon, ChevronsUpDownIcon } from "lucide-react"
import React from "react"

import { Button } from "@/components/ui/button"
import { Drawer, DrawerContent, DrawerTrigger } from "@/components/ui/drawer"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { useMediaQuery } from "@/hooks"
import { cn } from "@/lib/utils"

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../ui/command"
interface ComboBoxProps<T> {
  items: { label: string; value: T }[]
  selected?: T | null
  onSelect: (value: T) => void
  placeholder?: string
  placeholderCommand?: string
  disabled?: boolean
  align?: "start" | "end"
  /** Encaminhado ao gatilho: sem ele, um `<label htmlFor>` externo não abre nem nomeia o campo. */
  id?: string
}
export const ComboBox = <T extends string>({
  items,
  selected,
  onSelect,
  placeholder = "Select an option",
  placeholderCommand = "Pesquise por uma opção",
  disabled,
  align = "end",
  id,
}: ComboBoxProps<T>) => {
  const selectedItem = items.find((item) => item.value === selected)
  const [open, setOpen] = React.useState(false)
  const isDesktop = useMediaQuery("(min-width: 768px)")
  if (isDesktop) {
    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            id={id}
            variant="outline"
            type="button"
            role="combobox"
            disabled={disabled}
            className={cn(
              "h-10 w-full justify-between overflow-hidden",
              !selected && "text-muted-foreground",
            )}
          >
            {selectedItem ? (
              selectedItem.label
            ) : (
              <span className="truncate">{placeholder}</span>
            )}
            <ChevronsUpDownIcon size={16} className="shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent align={align}>
          <ListItems
            items={items}
            selected={selected}
            onSelect={onSelect}
            setOpen={setOpen}
            placeholderCommand={placeholderCommand}
          />
        </PopoverContent>
      </Popover>
    )
  }
  return (
    <Drawer open={open} onOpenChange={setOpen}>
      <DrawerTrigger asChild>
        <Button
          id={id}
          variant="outline"
          type="button"
          role="combobox"
          disabled={disabled}
          className={cn(
            "h-10 w-full justify-between overflow-hidden",
            !selected && "text-muted-foreground",
          )}
        >
          {selectedItem ? (
            selectedItem.label
          ) : (
            <span className="truncate">{placeholder}</span>
          )}
          <ChevronsUpDownIcon size={16} className="shrink-0 opacity-50" />
        </Button>
      </DrawerTrigger>
      <DrawerContent>
        <div className="mt-4 border-t">
          <ListItems
            items={items}
            selected={selected}
            onSelect={onSelect}
            setOpen={setOpen}
            placeholderCommand={placeholderCommand}
          />
        </div>
      </DrawerContent>
    </Drawer>
  )
}
interface ListItemsProps<T> {
  items: { label: string; value: T }[]
  selected?: T | null
  onSelect: (value: T) => void
  setOpen: (open: boolean) => void
  placeholderCommand?: string
}
const ListItems = <T extends string>({
  items,
  selected,
  onSelect,
  setOpen,
  placeholderCommand,
}: ListItemsProps<T>) => {
  return (
    <Command>
      <CommandInput placeholder={placeholderCommand} />
      <CommandList>
        <CommandEmpty>Opção não encontrada</CommandEmpty>
        <CommandGroup>
          {items.map((item, index) => (
            <CommandItem
              value={item.label}
              key={`${item.value}-${index}`}
              onSelect={() => {
                onSelect(item.value)
                setOpen(false)
              }}
            >
              <CheckIcon
                className={cn(
                  "mr-2 h-4 w-4",
                  item.value === selected ? "opacity-100" : "opacity-0",
                )}
              />
              {item.label}
            </CommandItem>
          ))}
        </CommandGroup>
      </CommandList>
    </Command>
  )
}
